import React from 'react';
import { Box, Tooltip, TooltipProps, Typography } from '@mui/material';
import { Icon } from '@ui/Icon';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { SemiRequired } from '@shared/models/type-helpers.model';

export type ISimpleBarChartTooltipData = {
  label: string;
  co2e: number;
  sek: number;
};

type SimpleBarChartTooltipProps = Partial<ISimpleBarChartTooltipData> &
  Omit<TooltipProps, 'title' | 'sek'> & { interactive?: boolean };

/**
 * Whether {@link ISimpleBarChartTooltipData} has enough for the styled lifecycle tooltip (e.g. A1-A3).
 * `co2e` may be zero; only non-finite / missing values suppress the tooltip.
 */
export function isSimpleBarChartTooltipDataComplete(
  data: Partial<ISimpleBarChartTooltipData> | undefined,
): data is Pick<ISimpleBarChartTooltipData, 'label' | 'co2e'> &
  Partial<Pick<ISimpleBarChartTooltipData, 'sek'>> {
  if (!data) return false;
  return Boolean(data.label?.trim()) && guards.is.finiteNumber(data.co2e);
}

export type SimpleBarChartTooltipBodyProps = SemiRequired<
  ISimpleBarChartTooltipData,
  'sek'
> & { color?: string };

/** Styled tooltip body (lifecycle title + CO₂ / cost rows) shared by per-segment and stacked-bar tooltips. */
export const SimpleBarChartTooltipBody: React.FC<
  SimpleBarChartTooltipBodyProps
> = ({ label, co2e, sek, color = 'secondary.light' }) => {
  const displayCurrency = useDisplayCurrency();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: 3,
        gap: 10,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography
          color={color}
          sx={{
            fontWeight: 600,
            fontSize: 20,
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
        <Typography color={color} variant="body2">
          per m²
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <TooltipRow
          icon={<Icon category="costEmission" icon="kgCO2e" />}
          value={co2e}
          color={color}
        />
        {displayCurrency.isEnabled && sek !== undefined && (
          <TooltipRow
            icon={<Icon category="currency" icon={displayCurrency.current} />}
            value={
              getDisplayValueData(
                { value: sek, unit: 'sek' },
                { outputUnit: displayCurrency.current },
              ).valueAsNumber
            }
            color={color}
          />
        )}
      </Box>
    </Box>
  );
};

/**
 * Wraps chart segments with a rich CO₂ / cost tooltip when `label` is non-empty and `co2e` is finite (including zero).
 */
export const SimpleBarChartTooltip: React.FC<SimpleBarChartTooltipProps> = ({
  children,
  label,
  co2e,
  sek,
  interactive = true,
  ...props
}) => {
  const payload = { label, co2e, sek };
  if (!interactive || !isSimpleBarChartTooltipDataComplete(payload))
    return children;
  return (
    <Tooltip
      {...props}
      title={
        <SimpleBarChartTooltipBody
          label={payload.label}
          co2e={payload.co2e}
          sek={payload.sek}
        />
      }
    >
      {children}
    </Tooltip>
  );
};

const TooltipRow: React.FC<{
  icon: React.ReactNode;
  value: number;
  color?: string;
}> = ({ icon, value, color }) => {
  const valueString = guards.is.finiteNumber(value)
    ? getDisplayNumber(value, { notation: 'compact' })
    : '-';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
      }}
    >
      {icon}
      <Typography
        color={color}
        sx={{
          fontWeight: 600,
          fontSize: 12,
        }}
      >
        {valueString}
      </Typography>
    </Box>
  );
};
